import Phaser from 'phaser';
import { PALETTE } from '../config';
import { fetchLeaderboard } from '../game/api';
import { DISCIPLINES } from '../game/data/disciplines';
import { t, disciplineLabel } from '../game/i18n';
import { SaveManager } from '../game/SaveManager';
import type { LeaderboardRecord } from '../game/types';
import { countryFlag } from '../game/data/flags';
import { STARTER_ROSTER } from '../game/data/athletes';
import { formatMetric } from '../game/data/metricFormat';
import { addBackButton } from '../ui/BackButton';
import { drawGradientBackground } from '../ui/Background';
import { drawPanel } from '../ui/Panel';
import { viewport } from '../ui/viewport';

type Row = Pick<LeaderboardRecord, 'playerName' | 'species' | 'metric' | 'athleteName' | 'nationality' | 'gold' | 'silver' | 'bronze'>;

/** Per-player "meta" boards shown ahead of the per-discipline ones. */
const META_BOARDS = ['medals', 'coins'];

/** Podium accent colours (gold / silver / bronze) for the top three rows. */
const MEDAL_COLORS = [0xffd24a, 0xccd6ea, 0xd58a52];

/** Distance-metric disciplines (everything else is a time). Meta boards are distance too. */
const DISTANCE_DISCIPLINES = new Set(['long_jump', 'javelin', 'high_jump', 'triple_jump', 'shot_put', 'hammer_throw', 'pole_vault', 'discus', 'weightlifting', 'diving', 'archery', 'coins', 'medals']);

/** Emoji per board for the compact selector. */
const DISCIPLINE_EMOJI: Record<string, string> = {
  medals: '🏅', coins: '🪙',
  sprint_100m: '🏃', long_jump: '🦘', hurdles: '🏃', javelin: '🎯', high_jump: '🤸',
  triple_jump: '🦘', shot_put: '🏋️', hammer_throw: '🔨', pole_vault: '🐱', discus: '🥏', weightlifting: '🏋️',
  swim_50m: '🏊', diving: '🤿', archery: '🏹',
};

/** Display name for any board id (meta boards aren't in DISCIPLINES). */
function boardLabel(id: string): string {
  if (id === 'medals') return t('lb.medals');
  if (id === 'coins') return t('lb.coins');
  return disciplineLabel(id);
}

/** Global leaderboards (other players) with a local fallback when offline. */
export class Leaderboard extends Phaser.Scene {
  private disciplineId = 'sprint_100m';
  private view: 'best' | 'all' = 'best';
  private rowObjects: Phaser.GameObjects.GameObject[] = [];
  private status?: Phaser.GameObjects.Text;
  private contentTop = 0;

  constructor() {
    super('Leaderboard');
  }

  init(data: { disciplineId?: string; view?: 'best' | 'all' }): void {
    if (data?.disciplineId) this.disciplineId = data.disciplineId;
    this.view = data?.view === 'all' ? 'all' : 'best';
  }

  private get isMetaBoard(): boolean {
    return META_BOARDS.includes(this.disciplineId);
  }

  create(): void {
    const { h, cx, s } = viewport(this);
    drawGradientBackground(this);
    this.rowObjects = [];

    this.add.text(cx, h * 0.055, `🏆 ${t('lb.title')}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(24, Math.round(32 * s))}px`, color: '#ffd24a', fontStyle: 'bold',
    }).setOrigin(0.5);

    // Discipline selector: the discipline's own card icon (as in the picker), with its
    // name small alongside as a caption, flanked by ‹ › arrows. One compact row.
    const ids = [...META_BOARDS, ...Object.keys(DISCIPLINES)];
    const idx = Math.max(0, ids.indexOf(this.disciplineId));
    const selY = h * 0.17;
    const cycle = (dir: number): void => {
      const next = ids[(idx + dir + ids.length) % ids.length]!;
      this.scene.restart({ disciplineId: next, view: this.view });
    };
    const iconSz = Math.min(72, Math.max(54, h * 0.18));
    const nameFs = Math.max(15, Math.round(18 * s));
    const countFs = Math.max(11, Math.round(12 * s));
    const name = this.add.text(0, 0, boardLabel(this.disciplineId), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${nameFs}px`, color: '#f4f7ff', fontStyle: 'bold' }).setOrigin(0, 0.5);
    const count = this.add.text(0, 0, `${idx + 1} / ${ids.length}`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${countFs}px`, color: '#9fb0e0' }).setOrigin(0, 0.5);
    const textW = Math.max(name.width, count.width);
    const gap = 12 * s;
    const groupW = iconSz + gap + textW;
    const startX = cx - groupW / 2;
    const iconCx = startX + iconSz / 2;
    const textX = startX + iconSz + gap;
    name.setPosition(textX, selY - 10 * s);
    count.setPosition(textX, selY + 12 * s);

    // Premium rounded icon tile: soft shadow, glass base, the discipline art cover-fit and
    // rounded to the whole tile, a top gloss and a thin light rim (no flat orange frame).
    const r = 18;
    const half = iconSz / 2;
    const tileX = iconCx - half, tileY = selY - half;
    this.add.graphics()
      .fillStyle(0x000000, 0.32).fillRoundedRect(tileX, tileY + 5, iconSz, iconSz, r)
      .fillStyle(PALETTE.bgBottom, 1).fillRoundedRect(tileX, tileY, iconSz, iconSz, r);
    const key = `card_${this.disciplineId}`;
    if (this.textures.exists(key)) {
      const img = this.add.image(iconCx, selY, key).setOrigin(0.5);
      const src = this.textures.get(key).getSourceImage();
      img.setScale(Math.max(iconSz / src.width, iconSz / src.height)); // cover-fit, fills the tile
      const m = this.make.graphics({ x: 0, y: 0 });
      m.fillStyle(0xffffff).fillRoundedRect(tileX, tileY, iconSz, iconSz, r);
      img.setMask(m.createGeometryMask());
    } else {
      this.add.text(iconCx, selY, DISCIPLINE_EMOJI[this.disciplineId] ?? '🏅', { fontSize: `${Math.round(iconSz * 0.5)}px` }).setOrigin(0.5);
    }
    // Gloss sheen + crisp rim, drawn over the art.
    this.add.graphics()
      .fillStyle(0xffffff, 0.12).fillRoundedRect(tileX + 3, tileY + 3, iconSz - 6, iconSz * 0.42, r - 5)
      .lineStyle(2, 0xffffff, 0.30).strokeRoundedRect(tileX, tileY, iconSz, iconSz, r)
      .lineStyle(3, PALETTE.cadence, 0.22).strokeRoundedRect(tileX - 1.5, tileY - 1.5, iconSz + 3, iconSz + 3, r + 1);

    const aR = Math.max(24, 28 * s);
    const arrow = (ax: number, dir: number): void => {
      const c = this.add.circle(ax, selY, aR, PALETTE.ink, 0.45).setStrokeStyle(2, PALETTE.cadence, 0.7);
      const t = this.add.text(ax, selY, dir < 0 ? '‹' : '›', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(36 * s)}px`, color: '#59d2ff', fontStyle: 'bold' }).setOrigin(0.5);
      [c, t].forEach((o) => o.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, () => cycle(dir)));
    };
    arrow(startX - aR - 8 * s, -1);
    arrow(startX + groupW + aR + 8 * s, 1);
    this.contentTop = selY + iconSz / 2 + 12 * s;

    // Per-discipline boards get a "best per player / all measures" segmented toggle. The meta
    // boards (medagliere, monete) are one-row-per-player by nature, so no toggle there.
    if (!this.isMetaBoard) {
      this.contentTop += this.drawViewToggle(cx, this.contentTop, s);
    }

    // Subtle status line (only meaningful when offline — no "global" clutter online).
    this.status = this.add.text(cx, this.contentTop, '', {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: '#7c88b4',
    }).setOrigin(0.5);

    addBackButton(this, s, () => this.scene.start('MainMenu'));

    void this.loadBoard();
  }

  /** Sleek segmented control (Migliori / Tutte le misure) — one pill, the active half filled. */
  private drawViewToggle(cx: number, top: number, s: number): number {
    const fs = Math.max(13, Math.round(14 * s));
    const padX = 16 * s;
    const h = Math.max(30, 34 * s);
    const opts: { v: 'best' | 'all'; label: string }[] = [
      { v: 'best', label: t('lb.best') },
      { v: 'all', label: t('lb.all') },
    ];
    const widths = opts.map((o) => {
      const t = this.add.text(-999, -999, o.label, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, fontStyle: 'bold' });
      const wv = t.width + padX * 2;
      t.destroy();
      return wv;
    });
    const totalW = widths[0]! + widths[1]!;
    const cy = top + h / 2 + 4 * s;
    const x0 = cx - totalW / 2;
    // One rounded track holding both segments.
    this.add.graphics().setDepth(1)
      .fillStyle(PALETTE.ink, 0.55).fillRoundedRect(x0, cy - h / 2, totalW, h, h / 2)
      .lineStyle(1.5, PALETTE.cadence, 0.35).strokeRoundedRect(x0, cy - h / 2, totalW, h, h / 2);
    let x = x0;
    opts.forEach((o, i) => {
      const wv = widths[i]!;
      const active = this.view === o.v;
      if (active) {
        const c = Phaser.Display.Color.IntegerToColor(PALETTE.laneAccent);
        this.add.graphics().setDepth(2)
          .fillStyle(c.clone().darken(8).color, 1).fillRoundedRect(x + 2, cy - h / 2 + 3, wv - 4, h - 6, (h - 6) / 2)
          .fillStyle(c.clone().lighten(12).color, 1).fillRoundedRect(x + 2, cy - h / 2 + 3, wv - 4, (h - 6) * 0.55, (h - 6) / 2);
      }
      const label = this.add.text(x + wv / 2, cy, o.label, {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color: active ? '#0b1020' : '#cfd8f5', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(3);
      if (!active) {
        label.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, () => this.scene.restart({ disciplineId: this.disciplineId, view: o.v }));
        this.add.zone(x + wv / 2, cy, wv, h).setInteractive({ useHandCursor: true }).setDepth(3)
          .on(Phaser.Input.Events.POINTER_DOWN, () => this.scene.restart({ disciplineId: this.disciplineId, view: o.v }));
      }
      x += wv;
    });
    return h + 14 * s;
  }

  private async loadBoard(): Promise<void> {
    const kind = DISTANCE_DISCIPLINES.has(this.disciplineId) ? 'distance' : 'time';
    // Cap both views at 10 (5 per column) — a tight, readable top-10 instead of squashing 12-16.
    const limit = 10;
    const online = await fetchLeaderboard(this.disciplineId, kind, limit, this.isMetaBoard ? 'best' : this.view);
    if (online) {
      this.render(online, kind, '');
    } else if (META_BOARDS.includes(this.disciplineId)) {
      // Offline: there's no global field for meta boards, but show the player's own tally.
      this.render(this.selfMetaRow(), kind, t('lb.offlineTeam'));
    } else {
      const local = SaveManager.instance.topRecords(this.disciplineId, kind, 12);
      this.render(local, kind, t('lb.offlineLocal'));
    }
  }

  /** A single row representing this player's own coins / medal tally (offline fallback). */
  private selfMetaRow(): Row[] {
    const save = SaveManager.instance;
    const top = [...save.roster][0];
    const tally = save.medalTally();
    return [{
      playerName: save.teamName || t('lb.myTeam'),
      species: top?.species ?? 'cheetah',
      athleteName: top?.name ?? '',
      nationality: top?.nationality ?? '',
      metric: this.disciplineId === 'coins' ? save.coins : tally.gold * 1e8 + tally.silver * 1e4 + tally.bronze,
      gold: tally.gold, silver: tally.silver, bronze: tally.bronze,
    }];
  }

  private render(rows: Row[], kind: 'time' | 'distance', sourceLabel: string): void {
    const { w, h, cx, s } = viewport(this);
    this.status?.setText(sourceLabel).setColor('#9fb0e0');

    const panelW = Math.min(960, w * 0.96);
    const top = this.contentTop + 10 * s;
    const bottom = h - Math.max(20, 24 * s);
    drawPanel(this, cx, (top + bottom) / 2, panelW, bottom - top, { radius: 18, fillAlpha: 0.7 });

    if (rows.length === 0) {
      this.rowObjects.push(this.add.text(cx, (top + bottom) / 2, t('lb.empty'), {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(18, Math.round(20 * s))}px`, color: '#cfd8f5', align: 'center', lineSpacing: 8,
      }).setOrigin(0.5));
      return;
    }

    const currentBySpecies = new Map(STARTER_ROSTER.map((a) => [a.species, a]));
    const myName = SaveManager.instance.teamName;

    rows = rows.slice(0, 10); // hard cap: max 10 total → 5 per column
    // Two columns on landscape once there are enough rows (uses the wide screen and keeps
    // rows comfortably tall instead of squashing many entries into a single column).
    const twoCol = rows.length > 6;
    const colGap = 18 * s;
    const colW = twoCol ? (panelW - 44 * s - colGap) / 2 : panelW - 44 * s;
    const perCol = twoCol ? Math.ceil(rows.length / 2) : rows.length;
    const listTop = top + 14 * s;
    const listH = bottom - listTop - 8 * s;
    const rowH = Phaser.Math.Clamp(listH / perCol, 42, 58);
    const col0X = cx - panelW / 2 + 22 * s;
    const col1X = twoCol ? col0X + colW + colGap : col0X;

    rows.forEach((r, i) => {
      const col = twoCol && i >= perCol ? 1 : 0;
      const rowInCol = col === 1 ? i - perCol : i;
      const colX = col === 1 ? col1X : col0X;
      const y = listTop + rowInCol * rowH + rowH / 2;
      this.drawRow(r, i, colX, colW, y, rowH, s, kind, currentBySpecies, myName);
    });
  }

  /** One premium leaderboard row: a card with a podium badge, ring avatar, names and metric. */
  private drawRow(
    r: Row, i: number, colX: number, colW: number, y: number, rowH: number, s: number,
    kind: 'time' | 'distance', currentBySpecies: Map<string, { name: string; nationality: string }>, myName: string,
  ): void {
    const mine = r.playerName === myName;
    const podium = this.view !== 'all' && i < 3; // medals only on the best/meta boards
    const medalCol = podium ? MEDAL_COLORS[i]! : undefined;
    const accent = mine ? PALETTE.laneAccent : medalCol;
    const teamFs = Math.max(16, Math.round(17 * s));
    const subFs = Math.max(13, Math.round(14 * s));
    const flagFs = Math.max(18, Math.round(20 * s));
    const metricFs = Math.max(17, Math.round(18 * s));

    // Card: shadow, tinted glass body, top gloss, left accent bar, rim.
    const cardX = colX - 12, cardY = y - rowH / 2 + 2, cardW = colW + 20, cardH = rowH - 4, rad = 12;
    const base = mine ? PALETTE.laneAccent : medalCol ?? PALETTE.bgTop;
    const baseA = mine ? 0.30 : medalCol ? 0.18 : i % 2 === 0 ? 0.12 : 0.07;
    const g = this.add.graphics();
    g.fillStyle(0x000000, 0.26).fillRoundedRect(cardX, cardY + 3, cardW, cardH, rad);
    g.fillStyle(base, baseA).fillRoundedRect(cardX, cardY, cardW, cardH, rad);
    g.fillStyle(0xffffff, 0.05).fillRoundedRect(cardX, cardY, cardW, cardH * 0.5, rad);
    if (accent) g.fillStyle(accent, 0.95).fillRoundedRect(cardX + 2, cardY + 5, 5, cardH - 10, 2.5);
    g.lineStyle(1.5, accent ?? PALETTE.cadence, accent ? 0.6 : 0.16).strokeRoundedRect(cardX, cardY, cardW, cardH, rad);
    this.rowObjects.push(g);

    // Drawn rank badge (no emoji): a coloured disc with the position number.
    const badgeR = Math.min(rowH * 0.32, 17);
    const badgeX = colX + badgeR + 4 * s;
    this.drawRankBadge(badgeX, y, badgeR, i, medalCol);

    const isMeta = this.disciplineId === 'coins' || this.disciplineId === 'medals';
    const metricX = colX + colW;
    const contentLeft = badgeX + badgeR + 8 * s;

    if (isMeta) {
      const reserve = this.disciplineId === 'medals' ? 150 * s : 96 * s;
      const team = this.add.text(contentLeft, y, mine ? `${r.playerName} (${t('common.you')})` : r.playerName, {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(17, Math.round(19 * s))}px`, color: mine ? '#ffe9a8' : '#f4f7ff', fontStyle: 'bold',
      }).setOrigin(0, 0.5);
      if (team.width > metricX - contentLeft - reserve) team.setScale((metricX - contentLeft - reserve) / team.width);
      this.rowObjects.push(team);
    } else {
      // Animal head avatar with a colored ring (gold/silver/bronze on the podium).
      const avRad = Math.min(rowH * 0.40, 22);
      const iconX = contentLeft + avRad;
      this.rowObjects.push(this.add.circle(iconX, y, avRad + 1.5, PALETTE.bgBottom, 0.7)
        .setStrokeStyle(2.5, accent ?? PALETTE.cadence, accent ? 0.9 : 0.5));
      this.drawAvatar(r.species, iconX, y, avRad);

      const cur = currentBySpecies.get(r.species);
      const athleteLabel = cur ? cur.name : r.athleteName || r.species;
      const flag = countryFlag(cur ? cur.nationality : r.nationality);
      const nameX = iconX + avRad + 12 * s;
      const nameMaxW = metricX - nameX - 64 * s;
      const team = this.add.text(nameX, y - rowH * 0.18, mine ? `${r.playerName} (${t('common.you')})` : r.playerName, {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${teamFs}px`, color: mine ? '#ffe9a8' : '#f4f7ff', fontStyle: 'bold',
      }).setOrigin(0, 0.5);
      if (team.width > nameMaxW) team.setScale(nameMaxW / team.width);
      this.rowObjects.push(team);
      this.rowObjects.push(this.add.text(nameX, y + rowH * 0.24, flag, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${flagFs}px` }).setOrigin(0, 0.5));
      this.rowObjects.push(this.add.text(nameX + flagFs * 1.5, y + rowH * 0.24, athleteLabel, {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${subFs}px`, color: '#aeb9e0',
      }).setOrigin(0, 0.5));
    }

    // Metric — formatted per board (coins / medal tally / time / distance).
    if (this.disciplineId === 'medals') {
      const txt = `🥇${r.gold ?? 0}  🥈${r.silver ?? 0}  🥉${r.bronze ?? 0}`;
      this.rowObjects.push(this.add.text(metricX, y, txt, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(14, Math.round(15 * s))}px`, color: '#f4f7ff', fontStyle: 'bold' }).setOrigin(1, 0.5));
    } else {
      const metric = this.disciplineId === 'coins' ? `${Math.round(r.metric)} 🪙` : formatMetric(this.disciplineId, kind, r.metric);
      this.rowObjects.push(this.add.text(metricX, y, metric, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${metricFs}px`, color: this.disciplineId === 'coins' ? '#ffd24a' : mine ? '#ffe9a8' : '#7fe0ff', fontStyle: 'bold' }).setOrigin(1, 0.5));
    }
  }

  /** A coloured disc with the position number — premium replacement for emoji medals. */
  private drawRankBadge(x: number, y: number, rad: number, i: number, medalCol?: number): void {
    const g = this.add.graphics();
    g.fillStyle(0x000000, 0.28).fillCircle(x, y + 2, rad);
    if (medalCol !== undefined) {
      const c = Phaser.Display.Color.IntegerToColor(medalCol);
      g.fillStyle(c.clone().darken(8).color, 1).fillCircle(x, y, rad);
      g.fillStyle(c.clone().lighten(20).color, 0.7).fillCircle(x, y - rad * 0.32, rad * 0.66);
      g.lineStyle(2, 0xffffff, 0.55).strokeCircle(x, y, rad);
    } else {
      g.fillStyle(PALETTE.ink, 0.65).fillCircle(x, y, rad);
      g.lineStyle(1.5, PALETTE.cadence, 0.3).strokeCircle(x, y, rad);
    }
    this.rowObjects.push(g);
    this.rowObjects.push(this.add.text(x, y, `${i + 1}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(rad * 1.05)}px`, color: medalCol !== undefined ? '#5a4410' : '#dfe7ff', fontStyle: 'bold',
    }).setOrigin(0.5));
  }

  /** Draw the species head inside a circle at (x,y) radius rad (cut head part, or
   * head-framed whole-body fallback). */
  private drawAvatar(species: string, x: number, y: number, rad: number): void {
    const headKey = `${species}_part_head`;
    const fullKey = `${species}_full`;
    const m = this.make.graphics({ x: 0, y: 0 });
    m.fillStyle(0xffffff).fillCircle(x, y, rad);
    const mask = m.createGeometryMask();
    if (this.textures.exists(headKey)) {
      const img = this.add.image(x, y, headKey);
      img.setScale((rad * 2) / Math.max(img.width, img.height)).setMask(mask);
      this.rowObjects.push(img);
    } else if (this.textures.exists(fullKey)) {
      const headFrac: Record<string, number> = { frog: 0.34, penguin: 0.46, octopus: 0.58, bear: 0.48, lemur: 0.42 };
      const hf = headFrac[species] ?? 0.45;
      const img = this.add.image(x, y - rad, fullKey).setOrigin(0.5, 0);
      img.setScale((rad * 2) / (hf * img.height)).setMask(mask);
      this.rowObjects.push(img);
    }
  }
}
